import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Converts any string to a safe URL slug.
 * Handles Māori macrons, diacritics, and other non-ASCII characters by
 * decomposing (NFD), stripping combining marks, then collapsing to hyphens.
 * e.g. "Auckland Inground Pools" → "auckland-inground-pools"
 *      "Āpiti Solar" → "apiti-solar"
 */
export function toSlug(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const LOWERCASE_WORDS = new Set([
  "a", "an", "the",
  "and", "but", "or", "for", "nor", "so", "yet",
  "at", "by", "in", "of", "on", "to", "up", "as", "with", "from",
]);
const UPPERCASE_ACRONYMS = new Set([
  "NZ", "AU", "GST", "HVAC", "ROI", "CRM", "AC", "PV", "FAQ", "IP", "ID", "TV", "PR", "PO", "ASAP",
]);

export function toTitleCase(str: string): string {
  if (!str) return "";
  return str
    .trim()
    .split(/\s+/)
    .map((word, index, arr) => {
      const upper = word.toUpperCase();
      if (UPPERCASE_ACRONYMS.has(upper)) return upper;
      if (/^[a-z]+[A-Z]/.test(word)) return word;
      const lower = word.toLowerCase();
      if (index === 0 || index === arr.length - 1 || !LOWERCASE_WORDS.has(lower)) {
        const isAllUppercase = word === word.toUpperCase() && word.length > 1;
        const rest = isAllUppercase ? word.slice(1).toLowerCase() : word.slice(1);
        return word.charAt(0).toUpperCase() + rest;
      }
      return lower;
    })
    .join(" ");
}

// Source keys are snake_case (google_ads, csv_import). Title-case each
// underscore-separated segment so the fallback reads as a clean label.
export function toTitleCaseSource(value: string): string {
  if (!value) return "";
  return value
    .split("_")
    .map((part) => toTitleCase(part))
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Initials for logo/avatar fallbacks when a client has no logo uploaded.
 * Trims, uppercases, and splits by code point so emoji/macrons don't break.
 * e.g. "kiwi heat pumps" → "K" (max 1) or "KH" (max 2)
 */
export function nameInitials(name: string, max = 1): string {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  const initials = words
    .slice(0, max)
    .map((word) => [...word][0]?.toUpperCase() ?? "")
    .join("");
  return initials || "?";
}

/** Format a Date as YYYY-MM-DD in the browser's local timezone (avoids UTC shift from toISOString). */
export function toLocalDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/** Format a Date as YYYY-MM-DDTHH:mm in the browser's local timezone, suitable for `<input type="datetime-local">`. */
export function toLocalDateTime(date: Date): string {
  return `${toLocalDate(date)}T${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

/** Snap an ISO timestamp to the nearest 5-minute slot. Returns null on invalid input. */
export function snapTo5Minutes(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return null;
  const ms = date.getTime();
  const fiveMin = 5 * 60_000;
  return new Date(Math.round(ms / fiveMin) * fiveMin).toISOString();
}

/** Hour options for the 5-minute picker. */
export const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => String(h).padStart(2, "0"));

/** Minute options for the 5-minute picker. */
export const FIVE_MINUTE_OPTIONS = Array.from({ length: 12 }, (_, m) => String(m * 5).padStart(2, "0"));

/** Default follow-up time when only a date is picked. */
export const DEFAULT_FOLLOW_UP_HOUR = 9;
export const DEFAULT_FOLLOW_UP_MINUTE = 0;

/** Combine a local YYYY-MM-DD date string and HH + MM pair into an ISO timestamp. Returns null on invalid input. */
export function combineLocalDateAndTime(dateStr: string, hourStr: string, minuteStr: string): string | null {
  if (!dateStr) return null;
  const hour = Number.parseInt(hourStr, 10);
  const minute = Number.parseInt(minuteStr, 10);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  const localIso = `${dateStr}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:00`;
  const date = new Date(localIso);
  return Number.isFinite(date.getTime()) ? date.toISOString() : null;
}
/** Format an ISO timestamp for display in the lead page next-action / snooze UI.
 *  Returns just the date when the time matches the default follow-up slot (09:00 local).
 *  Returns `null` for invalid input.
 */
export function formatNextActionDisplay(iso: string | null | undefined, locale = "en-NZ"): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return null;
  const datePart = date.toLocaleDateString(locale, { weekday: "short", day: "numeric", month: "short" });
  const isDefaultSlot = date.getHours() === DEFAULT_FOLLOW_UP_HOUR && date.getMinutes() === DEFAULT_FOLLOW_UP_MINUTE;
  if (isDefaultSlot) return datePart;
  const timePart = date.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });
  return `${datePart}, ${timePart}`;
}
